import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import AssessmentTemplateForm from "@/components/AssessmentTemplateForm";
import { SearchBar } from "./_shared";

const FIELD_TYPES = ["rating", "number", "text", "textarea", "select", "checklist"];

export default function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [previewValue, setPreviewValue] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const [t, tt] = await Promise.all([api.get("/assessment-templates"), api.get("/therapy-types")]);
      setTemplates(t.data || []);
      setTherapyTypes(tt.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const current = templates.find(t => t.id === selectedId) || templates[0];
  const therapyName = (id) => therapyTypes.find(t => t.id === id)?.name || "—";

  const updateCurrent = (next) => setTemplates(templates.map(t => t.id === next.id ? next : t));
  const updateField = (idx, key, value) => {
    const fields = [...(current.fields || [])];
    fields[idx] = { ...fields[idx], [key]: value };
    updateCurrent({ ...current, fields });
  };
  const addField = () => updateCurrent({
    ...current,
    fields: [...(current.fields || []), { key: `field_${Date.now()}`, label: "New field", type: "text" }],
  });
  const removeField = (idx) => updateCurrent({ ...current, fields: current.fields.filter((_, i) => i !== idx) });

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.put(`/assessment-templates/${current.id}`, {
        therapy_type_id: current.therapy_type_id,
        name: current.name,
        fields: current.fields,
      });
      toast.success("Template saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const filteredTemplates = (() => {
    const s = q.trim().toLowerCase();
    if (!s) return templates;
    return templates.filter(t =>
      (t.name || "").toLowerCase().includes(s)
      || therapyName(t.therapy_type_id).toLowerCase().includes(s)
    );
  })();

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1">
        <CardHeader className="gap-3">
          <CardTitle className="text-base font-display tracking-tight">Templates ({filteredTemplates.length}{q && ` / ${templates.length}`})</CardTitle>
          <SearchBar value={q} onChange={setQ} placeholder="Search by template or therapy name…" testId="templates-search" />
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {filteredTemplates.length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                {q ? `No templates match "${q}"` : "No templates yet."}
              </div>
            )}
            {filteredTemplates.map(t => (
              <button
                key={t.id}
                onClick={() => { setSelectedId(t.id); setPreviewValue({}); }}
                className={`w-full px-4 py-3 text-left hover:bg-muted/40 ${current?.id === t.id ? "bg-primary/5 border-l-2 border-primary" : ""}`}
                data-testid={`template-${t.id}`}
              >
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-xs text-muted-foreground">{therapyName(t.therapy_type_id)} · {(t.fields || []).length} fields</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        {current ? (
          <>
            <CardHeader className="flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-display tracking-tight">{current.name}</CardTitle>
                <div className="text-xs text-muted-foreground mt-0.5">Therapy: {therapyName(current.therapy_type_id)}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={addField} className="gap-1.5" data-testid="template-add-field"><Plus size={14} />Field</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="template-save">{saving ? "Saving…" : "Save"}</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div className="overline">Fields</div>
                {(current.fields || []).map((f, i) => (
                  <div key={f.key || i} className="grid grid-cols-12 gap-2 items-start p-2 border border-border rounded-md">
                    <Input className="col-span-3" value={f.key} onChange={e => updateField(i, "key", e.target.value)} placeholder="key" />
                    <Input className="col-span-4" value={f.label} onChange={e => updateField(i, "label", e.target.value)} placeholder="Label" />
                    <Select value={f.type} onValueChange={v => updateField(i, "type", v)}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card">
                        {FIELD_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {(f.type === "select" || f.type === "checklist") ? (
                      <Input className="col-span-2" value={(f.options || []).join(", ")} onChange={e => updateField(i, "options", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} placeholder="opt1, opt2" />
                    ) : (
                      <label className="col-span-2 flex items-center gap-1.5 text-xs px-2">
                        <Checkbox checked={!!f.required} onCheckedChange={v => updateField(i, "required", !!v)} />
                        Required
                      </label>
                    )}
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeField(i)}><Trash size={14} /></Button>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-md bg-muted/40 border border-border">
                <div className="overline mb-3">Preview</div>
                <AssessmentTemplateForm template={current} value={previewValue} onChange={setPreviewValue} />
              </div>
            </CardContent>
          </>
        ) : (
          <CardContent className="p-8 text-sm text-muted-foreground">Select a template to edit.</CardContent>
        )}
      </Card>
    </div>
  );
}
